/**
 * view-stack.ts — Central overlay-stack manager (Ticket #195, Phase 1).
 *
 * Provides a minimal, ordered stack so that overlays close in LIFO order
 * (last-opened closes first), Escape key and Android hardware back-button
 * both route through a single handler, and each overlay's teardown runs
 * exactly once.
 *
 * Phase 1 integrates: project-file-viewer.ts + projects-view.ts (openDetail).
 * Phase 2 will migrate the remaining ~50 ad-hoc overlay.remove() call sites.
 */

interface StackEntry {
  el: HTMLElement;
  onClose?: () => void;
  /** Guard: prevents double-close if closeTopmost() is called re-entrantly. */
  closed: boolean;
}

const stack: StackEntry[] = [];

// #195-Review: Escape/Back pausieren, solange ein Nicht-Stapel-Dialog
// (confirm/prompt/select-Sheet) im Vordergrund ist — sonst schließt Escape die
// darunterliegende migrierte View statt nur den Dialog.
let suspended = 0;
/** Stapel-Schließen (Escape/Back) pausieren, solange ein Fremd-Dialog offen ist. */
export function suspendStack(): void {
  suspended++;
}
/** Pause wieder aufheben (nie unter 0). */
export function resumeStack(): void {
  if (suspended > 0) suspended--;
}

/**
 * Register an already-DOM-mounted overlay on top of the stack.
 * onClose is called exactly once when the overlay is dismissed via the stack.
 */
export function pushView(
  el: HTMLElement,
  opts?: { onClose?: () => void },
): void {
  stack.push({ el, onClose: opts?.onClose, closed: false });
}

/**
 * Pop the topmost entry and invoke its onClose callback.
 * Returns true if an entry was closed, false if the stack was empty.
 * Guards against double-close: if the entry is already marked closed, it is
 * silently skipped and the next entry is tried.
 */
export function closeTopmost(): boolean {
  while (stack.length > 0) {
    const entry = stack.pop()!;
    if (entry.closed) {
      // Already closed by a direct call — skip and try the next one.
      continue;
    }
    entry.closed = true;
    try {
      entry.onClose?.();
    } catch {
      /* ignore teardown errors */
    }
    return true;
  }
  return false;
}

/** Current number of registered (unclosed) overlays. */
function stackDepth(): number {
  // Count only entries that have not been closed yet.
  return stack.filter((e) => !e.closed).length;
}

// ---------------------------------------------------------------------------
// Global Escape listener — attached once at module init.
// ---------------------------------------------------------------------------
(function attachEscapeListener() {
  if (typeof document === "undefined") return;
  document.addEventListener(
    "keydown",
    (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      if (suspended > 0) return;
      if (stackDepth() === 0) return;
      ev.preventDefault();
      closeTopmost();
    },
    { capture: true },
  );
})();

// ---------------------------------------------------------------------------
// Best-effort Android hardware back-button via Capacitor.
// iOS has no hardware back — this is a no-op there.
// ---------------------------------------------------------------------------
(function attachCapacitorBack() {
  try {
    const cap = (globalThis as Record<string, unknown>)["Capacitor"] as
      | {
          Plugins?: {
            App?: {
              addListener?: (
                event: string,
                cb: (ev: { canGoBack: boolean }) => void,
              ) => void;
            };
          };
        }
      | undefined;
    cap?.Plugins?.App?.addListener?.("backButton", (_ev) => {
      if (suspended > 0) return;
      if (stackDepth() > 0) {
        closeTopmost();
      }
      // If stack empty: let the OS handle the back action (app exit etc.)
    });
  } catch {
    /* Capacitor not available — ignore */
  }
})();
